import { NavLink, Outlet } from "react-router-dom";
import { Building2, Calculator, Database, Gauge, Landmark, SlidersHorizontal, Waves, Loader2, FileDown, LayoutDashboard, TrendingUp, Receipt, HardHat, GitCompare, GraduationCap, LineChart, ShieldCheck, ScrollText, Users, LogOut } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FilterBar } from "@/components/FilterBar";
import { DetailPanel } from "@/components/DetailPanel";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HorizonSelector } from "@/components/HorizonSelector";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { errMsg, http } from "@/lib/api";

const NAV = [
  { to: "/", label: "Executive Summary", icon: LayoutDashboard, id: "executive" },
  { to: "/group", label: "Group Financials", icon: Gauge, id: "group" },
  { to: "/entities", label: "Institution Analysis", icon: Building2, id: "entities" },
  { to: "/revenue", label: "Revenue", icon: TrendingUp, id: "revenue" },
  { to: "/expenses", label: "Expenses & EBITDA", icon: Receipt, id: "expenses" },
  { to: "/cash-flow", label: "Cash Flow", icon: Waves, id: "cash-flow" },
  { to: "/capex", label: "Capex & Projects", icon: HardHat, id: "capex" },
  { to: "/loans", label: "Debt & Interest", icon: Landmark, id: "loans" },
  { to: "/compare", label: "Version Comparison", icon: GitCompare, id: "compare" },
  { to: "/scenarios", label: "Scenario Planner", icon: SlidersHorizontal, id: "scenarios" },
  { label: "Fee Cohort Model", icon: GraduationCap, id: "cohort", soon: "Phase 3" },
  { label: "Forecast & Extrapolation", icon: LineChart, id: "forecast", soon: "Phase 4" },
  { to: "/assumptions", label: "Assumptions", icon: Calculator, id: "assumptions" },
  { to: "/validation", label: "Data Validation", icon: ShieldCheck, id: "validation" },
  { to: "/data", label: "Data & Versions", icon: Database, id: "data" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, id: "audit", roles: ["admin", "modeller"] },
  { to: "/users", label: "Users & Roles", icon: Users, id: "users", roles: ["admin"] },
];

const NavItems = ({ compact }) => {
  const { can } = useAuth();
  return NAV.filter((n) => !n.roles || can(...n.roles)).map(({ to, label, icon: Icon, id, soon }) => (soon ? (
    <div key={id} className="flex items-center gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-sm text-slate-600" data-testid={`nav-tab-${id}${compact ? "-mobile" : ""}`}>
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />{label}<span className="ml-auto text-[9px] uppercase">{soon}</span>
    </div>
  ) : (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      data-testid={`nav-tab-${id}${compact ? "-mobile" : ""}`}
      className={({ isActive }) =>
        `flex items-center gap-3 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors ${
          isActive ? "bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/25" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      {label}
    </NavLink>
  )));
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  return (
    <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="user-menu">
      <span className="hidden xl:inline">{user.email}</span>
      <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-300" data-testid="user-role-badge">{user.role}</span>
      <button onClick={logout} title="Sign out" className="hover:text-rose-400" data-testid="logout-button"><LogOut className="h-4 w-4" /></button>
    </div>
  );
};

const UpdatedDate = () => {
  const { data } = useData();
  const d = data?.version?.confirmed_at || data?.version?.uploaded_at;
  if (!d) return null;
  const txt = new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return <span className="whitespace-nowrap text-[11px] text-slate-400" title="Date the active workbook version was confirmed" data-testid="updated-date">Updated: {txt}</span>;
};

const VersionSwitcher = () => {
  const { data, versions, activate } = useData();
  const { can } = useAuth();
  const confirmed = versions.filter((v) => v.status === "confirmed");
  if (!can("admin", "modeller")) {
    return <span className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300" data-testid="version-switcher">{data?.version?.name}</span>;
  }
  const onChange = async (id) => {
    try {
      await activate(id);
      toast.success("Switched workbook version");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Select value={data?.version?.id} onValueChange={onChange}>
      <SelectTrigger data-testid="version-switcher" className="h-8 w-[220px] rounded-full border-slate-700 bg-slate-900/70 text-xs">
        <SelectValue placeholder="Version" />
      </SelectTrigger>
      <SelectContent className="bg-[#131c2e]">
        {confirmed.map((v) => (
          <SelectItem key={v.id} value={v.id} className="text-xs" data-testid={`version-option-${v.id}`}>{v.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const BoardPackButton = () => {
  const { data, horizon } = useData();
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await http.get(`/versions/${data.version.id}/board-pack`, { params: { start: horizon.from, end: horizon.to }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `SRM_Board_Pack_${data.version.name.replace(/\s+/g, "_")}_FY${data.model.years[horizon.from]}_to_FY${data.model.years[horizon.to]}.pdf`;
      a.click();
      toast.success("Board pack downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" onClick={download} disabled={busy || !data} data-testid="board-pack-export-button"
      className="h-8 rounded-full bg-amber-500 text-xs text-slate-950 hover:bg-amber-400">
      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileDown className="mr-1.5 h-3.5 w-3.5" />}Board pack PDF
    </Button>
  );
};

const FilterBanner = () => {
  const { data, filters, setFilters } = useData();
  const e = data.model.entities.find((x) => x.code === filters.entity);
  if (!e) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-amber-300" data-testid="global-filter-banner">
      <span>Showing <b>{e.name}</b> on every page. Group-only items (central cash balance, head-office funding) stay group-wide.</span>
      <button onClick={() => setFilters((f) => ({ ...f, entity: null }))} className="underline hover:text-amber-200" data-testid="global-filter-clear">Show whole group</button>
    </div>
  );
};

export default function Layout() {
  const { loading, error, data, filters } = useData();
  return (
    <div className="grain relative flex min-h-screen bg-[#0B0F19]">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-800/80 bg-[#0B0F19] px-4 py-6 lg:flex">
        <div className="mb-8 px-2">
          <div className="font-display text-xl font-bold tracking-tight text-slate-50">SRM<span className="text-amber-500">.</span>Group</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-slate-500">Management Dashboard</div>
        </div>
        <nav className="flex flex-col gap-0.5 overflow-y-auto"><NavItems /></nav>
        <div className="mt-4 rounded-lg border border-slate-800 p-3 text-[11px] leading-relaxed text-slate-500">
          All figures in <span className="text-amber-400">Rs. Crores</span>. Source: {data?.version?.filename || "—"}
        </div>
      </aside>
      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#0B0F19]/80 backdrop-blur-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 lg:px-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-lg font-bold text-slate-50 lg:hidden">SRM<span className="text-amber-500">.</span></span>
              <VersionSwitcher />
              <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-400 ring-1 ring-amber-500/25" data-testid="unit-badge">{{ cr: "₹ Crores", lakh: "₹ Lakhs", pct: "% view" }[filters.unit]}</span>
              <UpdatedDate />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <HorizonSelector />
              <BoardPackButton />
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 px-4 pb-2.5 lg:px-8"><FilterBar /></div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-2 lg:hidden"><NavItems compact /></nav>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          {loading && !data && (
            <div className="flex items-center gap-2 text-slate-400" data-testid="app-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading workbook model…</div>
          )}
          {error && !data && <div className="text-rose-400" data-testid="app-error">{error}</div>}
          {data && <FilterBanner />}
          {data && <Outlet />}
          {data && <DetailPanel />}
        </main>
      </div>
    </div>
  );
}
